import { MathUtils, Quaternion, Vector3 } from 'three';
import {
  clamp,
  expDamp,
  forwardOf,
  horizontalDistance,
  LOCAL_RIGHT,
  LOCAL_UP,
  LOCAL_FORWARD,
  lookRotation,
  randomBetween,
  randomSign,
  rotateTowards,
  rightOf,
  WORLD_UP,
} from '../utils/math';
import type {
  AircraftState,
  ControlFrame,
  EnemyAiState,
  FlareState,
  GameMode,
  LockState,
  MissileState,
  SimEvent,
} from './types';

const PLAYER_ID = 'player';
const BASE_ID = 'carrier';
const WORLD_RADIUS = 4700;
const LOCK_RANGE = 2600;
const LOCK_FOV = MathUtils.degToRad(34);
const LOCK_TIME = 1.15;
const PLAYER_START = new Vector3(8, 35.8, -188);
const BASE_POSITION = new Vector3(0, 18, -260);

const tmpForward = new Vector3();
const tmpDirection = new Vector3();
const tmpQuaternion = new Quaternion();
const tmpSegment = new Vector3();
const tmpToPoint = new Vector3();
const tmpClosest = new Vector3();

function distancePointToSegment(point: Vector3, start: Vector3, end: Vector3): number {
  tmpSegment.copy(end).sub(start);
  const segLenSq = tmpSegment.lengthSq();
  if (segLenSq <= 0.000001) return point.distanceTo(start);
  tmpToPoint.copy(point).sub(start);
  const t = clamp(tmpToPoint.dot(tmpSegment) / segLenSq, 0, 1);
  tmpClosest.copy(start).addScaledVector(tmpSegment, t);
  return point.distanceTo(tmpClosest);
}

export class GameSimulation {
  readonly basePosition = BASE_POSITION.clone();
  readonly enemies = new Map<string, AircraftState>();
  readonly missiles = new Map<string, MissileState>();
  readonly flares = new Map<string, FlareState>();
  readonly lock: LockState = { targetId: null, progress: 0, locked: false, range: 0 };

  player: AircraftState = this.createPlayer();
  mode: GameMode = 'ready';
  score = 0;
  wave = 0;
  baseHealth = 100;
  elapsed = 0;
  statusText = '战斗待命';
  pvpMode = false;

  private nextId = 1;
  private waveCooldown = 0;
  private manualTargetId: string | null = null;
  private boundaryWarnCooldown = 0;
  private playerLockWarnCooldown = 0;

  startGame(): SimEvent[] {
    this.reset('playing');
    return [
      { type: 'message', text: 'J-15 从航母甲板起飞，拦截来袭机群。', tone: 'success' },
    ];
  }

  setPvpMode(enabled: boolean): void {
    this.pvpMode = enabled;
  }

  reset(mode: GameMode = 'ready'): void {
    this.enemies.clear();
    this.missiles.clear();
    this.flares.clear();
    this.player = this.createPlayer();
    this.lock.targetId = null;
    this.lock.progress = 0;
    this.lock.locked = false;
    this.lock.range = 0;
    this.mode = mode;
    this.score = 0;
    this.wave = 0;
    this.baseHealth = 100;
    this.elapsed = 0;
    this.waveCooldown = mode === 'playing' ? 0.2 : 0;
    this.manualTargetId = null;
    this.statusText = mode === 'playing' ? '守住航母战斗群' : '战斗待命';
  }

  togglePause(): void {
    if (this.mode === 'playing') {
      this.mode = 'paused';
      this.statusText = '战斗暂停';
    } else if (this.mode === 'paused') {
      this.mode = 'playing';
      this.statusText = '继续拦截';
    }
  }

  step(rawDt: number, controls: ControlFrame): SimEvent[] {
    const events: SimEvent[] = [];
    const dt = clamp(rawDt, 0.001, 0.04);

    if (controls.start && this.mode !== 'playing') {
      events.push(...this.startGame());
    }

    if (controls.reset) {
      events.push(...this.startGame());
    }

    if (controls.pause) {
      this.togglePause();
      events.push({
        type: 'message',
        text: this.mode === 'paused' ? '战斗暂停。' : '恢复战斗。',
        tone: 'info',
      });
    }

    if (this.mode !== 'playing') {
      return events;
    }

    this.elapsed += dt;
    this.boundaryWarnCooldown = Math.max(0, this.boundaryWarnCooldown - dt);
    this.playerLockWarnCooldown = Math.max(0, this.playerLockWarnCooldown - dt);

    this.updatePlayer(dt, controls, events);
    if (this.mode !== 'playing') return events;
    if (!this.pvpMode) {
      this.updateLock(dt, controls, events);
      this.updatePlayerWeapons(dt, controls, events);
      this.updateEnemies(dt, events);
      this.updateMissiles(dt, events);
      this.updateFlares(dt);
      this.updateWaveFlow(dt, events);
      this.checkGameOver(events);
    } else {
      this.lock.targetId = null;
      this.lock.progress = 0;
      this.lock.locked = false;
      this.lock.range = 0;
      this.statusText = '联机交战区';
      this.player.cannonHeat = clamp(this.player.cannonHeat - 0.4 * dt, 0, 1);
    }

    return events;
  }

  getTarget(): AircraftState | null {
    return this.lock.targetId ? this.enemies.get(this.lock.targetId) ?? null : null;
  }

  getPlayerForward(target = new Vector3()): Vector3 {
    return forwardOf(this.player.quaternion, target);
  }

  emitPlayerCannonBurst(range = 920): SimEvent[] {
    const forward = forwardOf(this.player.quaternion, tmpForward);
    const origin = this.player.position.clone().addScaledVector(forward, 18);
    const end = origin.clone().addScaledVector(forward, range);
    return [{ type: 'gun-tracer', from: origin, to: end, hit: false, faction: 'player' }];
  }

  private createPlayer(): AircraftState {
    const quaternion = lookRotation(new Vector3(-0.16, 0, -1).normalize());
    return {
      id: PLAYER_ID,
      callsign: 'LANCE-1',
      faction: 'player',
      position: PLAYER_START.clone(),
      quaternion,
      velocity: new Vector3(-18, 0, -92),
      speed: 94,
      throttle: 0.62,
      health: 100,
      maxHealth: 100,
      radius: 24,
      missileAmmo: 6,
      flareAmmo: 8,
      cannonHeat: 0,
      afterburnerEnergy: 1,
      missileCooldown: 0,
      cannonCooldown: 0,
      flareCooldown: 0,
      alive: true,
      scoreValue: 0,
    };
  }

  private createEnemy(position: Vector3, wave: number, index: number): AircraftState {
    const desired = this.player.position.clone().sub(position).normalize();
    const health = 72 + wave * 9;
    const aggression = clamp(0.5 + wave * 0.045 + Math.random() * 0.25, 0.5, 0.95);
    const ai: EnemyAiState = {
      aggression,
      missileCooldown: randomBetween(2.4, 5.4),
      gunCooldown: randomBetween(0.2, 1.4),
      flareCooldown: randomBetween(2.2, 4.8),
      evadeTimer: 0,
      weave: randomBetween(0, Math.PI * 2),
      preferredRange: randomBetween(720, 1180),
    };

    return {
      id: `bandit-${this.nextId++}`,
      callsign: `BANDIT ${wave}-${index + 1}`,
      faction: 'enemy',
      position,
      quaternion: lookRotation(desired),
      velocity: desired.multiplyScalar(108 + wave * 3),
      speed: 108 + wave * 3,
      throttle: 0.8,
      health,
      maxHealth: health,
      radius: 21,
      missileAmmo: 2 + Math.floor(wave / 3),
      flareAmmo: 3,
      cannonHeat: 0,
      afterburnerEnergy: 1,
      missileCooldown: 0,
      cannonCooldown: 0,
      flareCooldown: 0,
      alive: true,
      scoreValue: 180 + wave * 35,
      ai,
    };
  }

  private updatePlayer(dt: number, controls: ControlFrame, events: SimEvent[]): void {
    const player = this.player;
    const healthRatio = player.health / Math.max(player.maxHealth, 1);
    const controlDamage = clamp((0.62 - healthRatio) / 0.62, 0, 1);
    player.missileCooldown = Math.max(0, player.missileCooldown - dt);
    player.cannonCooldown = Math.max(0, player.cannonCooldown - dt);
    player.flareCooldown = Math.max(0, player.flareCooldown - dt);

    player.throttle = clamp(player.throttle + controls.throttleDelta * dt * 0.55, 0.45, 1);
    const afterburner = controls.afterburner && player.afterburnerEnergy > 0.04;
    player.afterburnerEnergy = clamp(
      player.afterburnerEnergy + (afterburner ? -0.34 : 0.18) * dt,
      0,
      1,
    );

    const controlFactor = 1 - controlDamage * 0.42;
    const pitchRate = controls.pitch * 1.34 * controlFactor;
    const rollRate = controls.roll * 2.38 * controlFactor;
    const yawRate = controls.yaw * 0.82 * (1 - controlDamage * 0.34);
    player.quaternion.multiply(tmpQuaternion.setFromAxisAngle(LOCAL_RIGHT, pitchRate * dt));
    player.quaternion.multiply(tmpQuaternion.setFromAxisAngle(LOCAL_UP, -yawRate * dt));
    player.quaternion.multiply(tmpQuaternion.setFromAxisAngle(LOCAL_FORWARD, -rollRate * dt));
    player.quaternion.normalize();

    const targetSpeed = (72 + player.throttle * 128 + (afterburner ? 72 : 0)) * (1 - controlDamage * 0.14);
    player.speed = expDamp(player.speed, targetSpeed, afterburner ? 1.55 : 0.9, dt);
    forwardOf(player.quaternion, tmpForward);
    player.velocity.copy(tmpForward).multiplyScalar(player.speed);
    player.position.addScaledVector(player.velocity, dt);

    if (player.position.y < 34 && !this.isPlayerOnCarrierDeck()) {
      this.crashPlayer('crash', events, '海面撞击，座机坠毁。');
      return;
    }

    if (player.position.y > 2600) {
      player.position.y = 2600;
      player.speed *= 0.997;
    }

    this.applyWorldBounds(player, dt, events);
    this.checkPlayerCollision(events);
  }

  private updateLock(dt: number, controls: ControlFrame, events: SimEvent[]): void {
    const candidates = [...this.enemies.values()].filter((enemy) => enemy.alive);
    if (!candidates.length) {
      this.lock.targetId = null;
      this.lock.progress = Math.max(0, this.lock.progress - dt * 1.8);
      this.lock.locked = false;
      this.lock.range = 0;
      return;
    }

    candidates.sort((a, b) => a.position.distanceToSquared(this.player.position) - b.position.distanceToSquared(this.player.position));

    if (controls.targetNext) {
      const currentIndex = candidates.findIndex((enemy) => enemy.id === this.manualTargetId);
      this.manualTargetId = candidates[(currentIndex + 1 + candidates.length) % candidates.length]?.id ?? null;
      const target = this.manualTargetId ? this.enemies.get(this.manualTargetId) : null;
      if (target) events.push({ type: 'message', text: `雷达指定 ${target.callsign}`, tone: 'info' });
    }

    const forward = forwardOf(this.player.quaternion, new Vector3());
    let best: AircraftState | null = null;
    let bestScore = -Infinity;

    for (const enemy of candidates) {
      const toEnemy = enemy.position.clone().sub(this.player.position);
      const range = toEnemy.length();
      const dot = forward.dot(toEnemy.normalize());
      const manualBias = enemy.id === this.manualTargetId ? 0.32 : 0;
      const inSearchCone = dot > Math.cos(LOCK_FOV * (enemy.id === this.manualTargetId ? 1.85 : 1));
      if (range > LOCK_RANGE * (enemy.id === this.manualTargetId ? 1.2 : 1) || !inSearchCone) continue;
      const score = dot * 2.4 + (1 - range / LOCK_RANGE) + manualBias;
      if (score > bestScore) {
        bestScore = score;
        best = enemy;
      }
    }

    if (!best) {
      this.lock.progress = Math.max(0, this.lock.progress - dt * 1.4);
      if (this.lock.progress <= 0.02) this.lock.targetId = null;
      this.lock.locked = false;
      this.lock.range = 0;
      return;
    }

    const toTarget = best.position.clone().sub(this.player.position);
    const range = toTarget.length();
    const dot = forward.dot(toTarget.normalize());
    const previousId = this.lock.targetId;
    this.lock.targetId = best.id;
    this.lock.range = range;

    if (previousId !== best.id) {
      this.lock.progress = Math.max(0.08, this.lock.progress * 0.35);
    }

    const aspect = clamp((dot - 0.54) / 0.46, 0, 1);
    const rangeQuality = clamp(1 - range / (LOCK_RANGE * 1.15), 0.18, 1);
    this.lock.progress = clamp(this.lock.progress + (dt / LOCK_TIME) * aspect * rangeQuality, 0, 1);
    this.lock.locked = this.lock.progress >= 0.99;
  }

  private updatePlayerWeapons(dt: number, controls: ControlFrame, events: SimEvent[]): void {
    const player = this.player;

    if (controls.fireMissile) {
      if (this.lock.locked && this.lock.targetId && player.missileAmmo > 0 && player.missileCooldown <= 0) {
        this.spawnMissile(player, this.lock.targetId, events);
        player.missileAmmo -= 1;
        player.missileCooldown = 0.82;
      } else if (player.missileCooldown <= 0.05) {
        events.push({
          type: 'message',
          text: player.missileAmmo <= 0 ? '导弹耗尽，等待下一波补给。' : '需要稳定锁定后才能发射。',
          tone: 'warn',
        });
        player.missileCooldown = 0.28;
      }
    }

    if (controls.deployFlare && player.flareAmmo > 0 && player.flareCooldown <= 0) {
      this.spawnFlareBurst(player, 6, events);
      player.flareAmmo -= 1;
      player.flareCooldown = 1.05;
      events.push({ type: 'message', text: '热焰弹释放。', tone: 'info' });
    }

    if (controls.fireCannon && player.cannonCooldown <= 0 && player.cannonHeat < 0.98) {
      this.fireCannon(player, events);
      player.cannonCooldown = 0.065;
      player.cannonHeat = clamp(player.cannonHeat + 0.065, 0, 1);
    }

    const cooling = controls.fireCannon ? 0.18 : 0.46;
    player.cannonHeat = clamp(player.cannonHeat - cooling * dt, 0, 1);
  }

  private updateEnemies(dt: number, events: SimEvent[]): void {
    for (const enemy of [...this.enemies.values()]) {
      if (!enemy.ai || !enemy.alive) continue;
      const ai = enemy.ai;
      ai.missileCooldown = Math.max(0, ai.missileCooldown - dt);
      ai.gunCooldown = Math.max(0, ai.gunCooldown - dt);
      ai.flareCooldown = Math.max(0, ai.flareCooldown - dt);
      ai.evadeTimer = Math.max(0, ai.evadeTimer - dt);
      ai.weave += dt * (0.72 + ai.aggression * 0.6);

      const inbound = this.findInboundMissile(enemy);
      if (inbound && enemy.flareAmmo > 0 && ai.flareCooldown <= 0) {
        this.spawnFlareBurst(enemy, 4, events);
        enemy.flareAmmo -= 1;
        ai.flareCooldown = randomBetween(3.5, 6.5);
        ai.evadeTimer = randomBetween(0.8, 1.6);
      }

      const toPlayer = this.player.position.clone().sub(enemy.position);
      const healthRatio = enemy.health / Math.max(enemy.maxHealth, 1);
      const damagePenalty = clamp((0.55 - healthRatio) / 0.55, 0, 1);
      const playerDistance = toPlayer.length();
      const baseDistance = horizontalDistance(enemy.position, this.basePosition);
      const targetPosition = playerDistance < 2400 ? this.player.position : this.basePosition;
      const toTarget = targetPosition.clone().sub(enemy.position);
      const side = new Vector3(Math.sin(ai.weave), Math.sin(ai.weave * 0.7) * 0.28, Math.cos(ai.weave * 0.83));
      side.multiplyScalar(ai.evadeTimer > 0 ? 520 : 260);

      let desired = toTarget;
      if (playerDistance < ai.preferredRange * 0.62) {
        desired = enemy.position.clone().sub(this.player.position).add(side.multiplyScalar(1.3));
      } else if (playerDistance < ai.preferredRange * 1.15) {
        desired = this.player.position.clone()
          .add(forwardOf(this.player.quaternion, new Vector3()).multiplyScalar(180))
          .sub(enemy.position)
          .add(side);
      } else {
        desired.add(side);
      }

      if (horizontalDistance(enemy.position, this.basePosition) > WORLD_RADIUS * 1.04) {
        desired = this.basePosition.clone().sub(enemy.position);
      }

      rotateTowards(enemy.quaternion, desired, (0.72 + ai.aggression * 0.76) * (1 - damagePenalty * 0.34) * dt, tmpQuaternion);
      const targetSpeed = (ai.evadeTimer > 0 ? 188 : 94 + ai.aggression * 62) * (1 - damagePenalty * 0.16);
      enemy.speed = expDamp(enemy.speed, targetSpeed, 0.78 + ai.aggression, dt);
      forwardOf(enemy.quaternion, tmpForward);
      enemy.velocity.copy(tmpForward).multiplyScalar(enemy.speed);
      enemy.position.addScaledVector(enemy.velocity, dt);
      enemy.position.y = clamp(enemy.position.y, 70, 2100);

      const enemyForward = forwardOf(enemy.quaternion, new Vector3());
      const toPlayerNorm = this.player.position.clone().sub(enemy.position);
      const range = toPlayerNorm.length();
      toPlayerNorm.normalize();
      const dot = enemyForward.dot(toPlayerNorm);

      if (dot > 0.95 && range > 300 && range < 2200 && this.playerLockWarnCooldown <= 0) {
        events.push({ type: 'message', text: '敌方雷达锁定警告。', tone: 'danger' });
        this.playerLockWarnCooldown = 2.4;
      }

      if (dot > 0.93 && range > 280 && range < 1900 && ai.missileCooldown <= 0 && enemy.missileAmmo > 0) {
        this.spawnMissile(enemy, PLAYER_ID, events);
        enemy.missileAmmo -= 1;
        ai.missileCooldown = randomBetween(4.2, 7.5) - ai.aggression;
      }

      if (dot > 0.986 && range < 610 && ai.gunCooldown <= 0) {
        const from = enemy.position.clone().addScaledVector(enemyForward, 18);
        const to = this.player.position.clone().add(new Vector3(randomBetween(-12, 12), randomBetween(-8, 8), randomBetween(-12, 12)));
        events.push({ type: 'gun-tracer', from, to, hit: true, faction: 'enemy' });
        this.damagePlayer(4.2 + this.wave * 0.12, events);
        ai.gunCooldown = randomBetween(0.12, 0.22);
      }

      if (baseDistance < 260 && ai.gunCooldown <= 0.03) {
        this.baseHealth = clamp(this.baseHealth - (1.2 + this.wave * 0.12), 0, 100);
        events.push({ type: 'message', text: '敌机正在攻击航母战斗群。', tone: 'danger' });
        ai.gunCooldown = 0.9;
      }
    }
  }

  private updateMissiles(dt: number, events: SimEvent[]): void {
    for (const missile of [...this.missiles.values()]) {
      missile.age += dt;
      missile.fuel = Math.max(0, missile.fuel - dt);
      const previousPosition = missile.position.clone();

      const decoy = this.findBestFlareFor(missile);
      if (decoy) missile.targetFlareId = decoy.id;
      const targetFlare = missile.targetFlareId ? this.flares.get(missile.targetFlareId) ?? null : null;
      const targetAircraft = !targetFlare ? this.resolveMissileTarget(missile) : null;

      let targetPosition: Vector3 | null = null;
      let targetVelocity = new Vector3();
      if (targetFlare) {
        targetPosition = targetFlare.position;
        targetVelocity = targetFlare.velocity;
      } else if (targetAircraft?.alive) {
        targetPosition = targetAircraft.position;
        targetVelocity = targetAircraft.velocity;
      }

      if (targetPosition) {
        const toTarget = targetPosition.clone().sub(missile.position);
        const range = toTarget.length();
        const seekAngle = missile.direction.angleTo(toTarget);
        const playerMissile = missile.faction === 'player';
        if (seekAngle < missile.seekerFov || missile.lockLost < (playerMissile ? 1.65 : 1.05)) {
          const lead = targetPosition.clone().addScaledVector(targetVelocity, clamp(range / Math.max(missile.speed, 1), 0, 1.3));
          const desired = lead.sub(missile.position);
          const turn = missile.turnRate * (missile.fuel > 0 ? (playerMissile ? 1.08 : 0.92) : (playerMissile ? 0.68 : 0.54)) * dt;
          const q = lookRotation(missile.direction);
          rotateTowards(q, desired, turn);
          forwardOf(q, missile.direction);
          missile.lockLost = seekAngle < missile.seekerFov * (playerMissile ? 1.34 : 1.22) ? 0 : missile.lockLost + dt * (playerMissile ? 0.18 : 0.35);
        } else {
          missile.lockLost += dt * (targetFlare ? 1.65 : (playerMissile ? 0.18 : 0.35));
        }
      } else {
        missile.lockLost += dt;
      }

      const targetSpeed = missile.faction === 'player'
        ? (missile.fuel > 0 ? 395 : 288)
        : (missile.fuel > 0 ? 330 : 245);
      missile.speed = expDamp(missile.speed, targetSpeed, 1.55, dt);
      missile.velocity.copy(missile.direction).multiplyScalar(missile.speed);
      missile.position.addScaledVector(missile.velocity, dt);

      if (targetFlare && missile.position.distanceTo(targetFlare.position) < 26) {
        this.flares.delete(targetFlare.id);
        this.explodeMissile(missile, events, false);
        continue;
      }

      if (targetAircraft && missile.age > missile.armedAfter) {
        const hitRadius = targetAircraft.radius + (missile.faction === 'player' ? 34 : 24);
        const sweptDistance = distancePointToSegment(targetAircraft.position, previousPosition, missile.position);
        if (sweptDistance < hitRadius) {
          this.explodeMissile(missile, events, true);
          continue;
        }
      }

      if (missile.age > missile.life || missile.position.y < 0 || missile.lockLost > (missile.faction === 'player' ? 3.6 : 2.6)) {
        this.explodeMissile(missile, events, false);
      }
    }
  }

  private updateFlares(dt: number): void {
    for (const flare of [...this.flares.values()]) {
      flare.age += dt;
      flare.heat = clamp(1 - flare.age / flare.life, 0, 1);
      flare.velocity.y -= 14 * dt;
      flare.velocity.multiplyScalar(1 - dt * 0.08);
      flare.position.addScaledVector(flare.velocity, dt);
      if (flare.age >= flare.life || flare.position.y < 4) {
        this.flares.delete(flare.id);
      }
    }
  }

  private updateWaveFlow(dt: number, events: SimEvent[]): void {
    if (this.enemies.size > 0) {
      this.statusText = `第 ${this.wave} 波 | 敌机 ${this.enemies.size}`;
      return;
    }

    this.waveCooldown -= dt;
    this.statusText = this.wave === 0
      ? '雷达搜索中'
      : `下一波来袭 ${Math.max(0, Math.ceil(this.waveCooldown))}s`;

    if (this.waveCooldown <= 0) {
      this.spawnWave(events);
    }
  }

  private spawnWave(events: SimEvent[]): void {
    this.wave += 1;
    const count = Math.min(2 + this.wave, 8);
    const radius = randomBetween(2100, 3100) + this.wave * 70;
    const startAngle = randomBetween(0, Math.PI * 2);

    for (let i = 0; i < count; i += 1) {
      const angle = startAngle + (i / count) * Math.PI * 1.5 + randomBetween(-0.35, 0.35);
      const position = new Vector3(
        Math.cos(angle) * radius,
        randomBetween(360, 820) + this.wave * 14,
        Math.sin(angle) * radius,
      );
      this.enemies.set(`bandit-${this.nextId}`, this.createEnemy(position, this.wave, i));
    }

    this.waveCooldown = 999;
    events.push({ type: 'wave', wave: this.wave, count });
    events.push({ type: 'message', text: `第 ${this.wave} 波接近，${count} 架敌机。`, tone: 'warn' });
  }

  private spawnMissile(owner: AircraftState, targetId: string, events: SimEvent[]): void {
    const forward = forwardOf(owner.quaternion, new Vector3());
    const right = rightOf(owner.quaternion, new Vector3());
    const side = owner.faction === 'player' ? (owner.missileAmmo % 2 === 0 ? -1 : 1) : randomSign();
    const position = owner.position.clone()
      .addScaledVector(forward, 24)
      .addScaledVector(right, side * 6)
      .addScaledVector(WORLD_UP, -2);
    const id = `missile-${this.nextId++}`;
    const missile: MissileState = {
      id,
      faction: owner.faction,
      ownerId: owner.id,
      targetId,
      targetFlareId: null,
      position,
      direction: forward.clone(),
      velocity: forward.clone().multiplyScalar(owner.faction === 'player' ? 260 : 220),
      speed: owner.faction === 'player' ? 260 : 220,
      damage: owner.faction === 'player' ? 70 : 34,
      age: 0,
      life: owner.faction === 'player' ? 9.2 : 8.4,
      fuel: owner.faction === 'player' ? 3.1 : 2.9,
      turnRate: owner.faction === 'player' ? 2.95 : 2.08,
      seekerFov: owner.faction === 'player' ? MathUtils.degToRad(48) : MathUtils.degToRad(34),
      lockLost: 0,
      armedAfter: 0.18,
    };
    this.missiles.set(id, missile);
    events.push({ type: 'missile-fired', missileId: id, faction: owner.faction });
    events.push({
      type: 'message',
      text: owner.faction === 'player' ? 'Fox Two，导弹离架。' : '导弹告警！释放热焰弹或急转脱锁。',
      tone: owner.faction === 'player' ? 'success' : 'danger',
    });
  }

  private fireCannon(owner: AircraftState, events: SimEvent[]): void {
    const forward = forwardOf(owner.quaternion, new Vector3());
    const origin = owner.position.clone().addScaledVector(forward, 18);
    const solution = owner.faction === 'player'
      ? this.findGunSolution(owner, [...this.enemies.values()], 920, 22)
      : this.findGunSolution(owner, [this.player], 650, 18);
    const end = solution?.target
      ? solution.point
      : origin.clone().addScaledVector(forward, owner.faction === 'player' ? 920 : 650);

    events.push({ type: 'gun-tracer', from: origin, to: end.clone(), hit: Boolean(solution), faction: owner.faction });

    if (!solution?.target) return;

    if (solution.target.id === PLAYER_ID) {
      this.damagePlayer(4.5, events);
      return;
    }

    this.damageEnemy(solution.target, 9.2, events);
  }

  private checkPlayerCollision(events: SimEvent[]): void {
    if (!this.player.alive || this.mode !== 'playing') return;
    const rel = this.player.position.clone().sub(this.basePosition);
    const onDeck = this.isPlayerOnCarrierDeck();
    const hitCarrierHull = !onDeck && Math.abs(rel.x) < 78 && rel.y < 30 && Math.abs(rel.z) < 232;
    const hitCarrierIsland = rel.x > 26 && rel.x < 62 && rel.y < 70 && rel.z > 24 && rel.z < 92;
    if (hitCarrierHull || hitCarrierIsland) {
      this.crashPlayer('crash', events, '撞上航母甲板，座机坠毁。');
      return;
    }

    for (const enemy of this.enemies.values()) {
      if (!enemy.alive) continue;
      if (enemy.position.distanceTo(this.player.position) < this.player.radius + enemy.radius - 4) {
        this.damageEnemy(enemy, enemy.health, events);
        this.crashPlayer('crash', events, '空中相撞，座机坠毁。');
        return;
      }
    }
  }

  private findGunSolution(
    owner: AircraftState,
    targets: AircraftState[],
    maxRange: number,
    tolerance: number,
  ): { target: AircraftState; point: Vector3 } | null {
    const forward = forwardOf(owner.quaternion, new Vector3());
    let best: { target: AircraftState; point: Vector3; score: number } | null = null;

    for (const target of targets) {
      if (!target.alive || target.id === owner.id) continue;
      const toTarget = target.position.clone().sub(owner.position);
      const along = toTarget.dot(forward);
      if (along < 0 || along > maxRange) continue;
      const closest = owner.position.clone().addScaledVector(forward, along);
      const miss = closest.distanceTo(target.position);
      if (miss > target.radius + tolerance) continue;
      const score = along + miss * 18;
      if (!best || score < best.score) {
        best = { target, point: closest, score };
      }
    }

    return best ? { target: best.target, point: best.point } : null;
  }

  private spawnFlareBurst(owner: AircraftState, count: number, events: SimEvent[]): void {
    const forward = forwardOf(owner.quaternion, new Vector3());
    const right = rightOf(owner.quaternion, new Vector3());
    for (let i = 0; i < count; i += 1) {
      const upImpulse = randomBetween(6, 38);
      const sideImpulse = randomBetween(-42, 42);
      const id = `flare-${this.nextId++}`;
      const flare: FlareState = {
        id,
        ownerId: owner.id,
        faction: owner.faction,
        position: owner.position.clone()
          .addScaledVector(forward, -20)
          .addScaledVector(right, sideImpulse * 0.04)
          .addScaledVector(WORLD_UP, randomBetween(-3, 3)),
        velocity: owner.velocity.clone()
          .addScaledVector(forward, -96 - Math.random() * 34)
          .addScaledVector(right, sideImpulse)
          .addScaledVector(WORLD_UP, upImpulse),
        heat: 1,
        age: 0,
        life: randomBetween(2.0, 2.9),
      };
      this.flares.set(id, flare);
    }
    events.push({ type: 'explosion', position: owner.position.clone().addScaledVector(forward, -18), radius: 24, tone: 'small' });
  }

  private findInboundMissile(enemy: AircraftState): MissileState | null {
    for (const missile of this.missiles.values()) {
      if (missile.faction !== 'player' || missile.targetId !== enemy.id) continue;
      if (missile.position.distanceTo(enemy.position) < 620) return missile;
    }
    return null;
  }

  private findBestFlareFor(missile: MissileState): FlareState | null {
    let best: FlareState | null = null;
    let bestScore = 0;
    for (const flare of this.flares.values()) {
      if (flare.faction === missile.faction || flare.heat <= 0.05) continue;
      tmpDirection.copy(flare.position).sub(missile.position);
      const distance = tmpDirection.length();
      if (distance > 740 || distance < 1) continue;
      const dot = missile.direction.dot(tmpDirection.normalize());
      if (dot < Math.cos(missile.seekerFov * 1.55)) continue;
      const score = flare.heat * dot * (1 - distance / 760);
      if (score > bestScore) {
        bestScore = score;
        best = flare;
      }
    }
    return bestScore > 0.08 ? best : null;
  }

  private resolveMissileTarget(missile: MissileState): AircraftState | null {
    if (missile.targetId === PLAYER_ID) return this.player.alive ? this.player : null;
    if (!missile.targetId) return null;
    return this.enemies.get(missile.targetId) ?? null;
  }

  private explodeMissile(missile: MissileState, events: SimEvent[], applyDamage: boolean): void {
    this.missiles.delete(missile.id);
    const blastRadius = missile.faction === 'player' ? 72 : 58;
    events.push({
      type: 'explosion',
      position: missile.position.clone(),
      radius: blastRadius,
      tone: applyDamage ? 'large' : 'small',
    });

    if (!applyDamage) return;

    if (missile.faction === 'player') {
      for (const enemy of [...this.enemies.values()]) {
        const distance = enemy.position.distanceTo(missile.position);
        if (distance > blastRadius + enemy.radius) continue;
        const scale = clamp(1 - distance / (blastRadius + enemy.radius), 0.2, 1);
        this.damageEnemy(enemy, missile.damage * scale, events);
      }
      return;
    }

    const distanceToPlayer = this.player.position.distanceTo(missile.position);
    if (distanceToPlayer < blastRadius + this.player.radius) {
      const scale = clamp(1 - distanceToPlayer / (blastRadius + this.player.radius), 0.22, 1);
      this.damagePlayer(missile.damage * scale, events);
    }
  }

  private damageEnemy(enemy: AircraftState, amount: number, events: SimEvent[]): void {
    enemy.health = clamp(enemy.health - amount, 0, enemy.maxHealth);
    events.push({ type: 'damage', targetId: enemy.id, amount, faction: 'player' });
    if (enemy.health <= 0 && enemy.alive) {
      enemy.alive = false;
      this.enemies.delete(enemy.id);
      this.score += enemy.scoreValue;
      events.push({ type: 'kill', targetId: enemy.id, score: enemy.scoreValue });
      events.push({ type: 'explosion', position: enemy.position.clone(), radius: 92, tone: 'large' });
      events.push({ type: 'message', text: `${enemy.callsign} 击落 +${enemy.scoreValue}`, tone: 'success' });

      if (this.lock.targetId === enemy.id) {
        this.lock.targetId = null;
        this.lock.locked = false;
        this.lock.progress = 0;
      }

      if (this.enemies.size === 0) {
        this.waveCooldown = 4.2;
        this.resupplyAfterWave(events);
      }
    }
  }

  private damagePlayer(amount: number, events: SimEvent[]): void {
    this.player.health = clamp(this.player.health - amount, 0, this.player.maxHealth);
    events.push({ type: 'damage', targetId: PLAYER_ID, amount, faction: 'enemy' });
  }

  private resupplyAfterWave(events: SimEvent[]): void {
    this.player.health = clamp(this.player.health + 24, 0, this.player.maxHealth);
    this.player.missileAmmo = Math.min(8, this.player.missileAmmo + 3);
    this.player.flareAmmo = Math.min(10, this.player.flareAmmo + 2);
    this.baseHealth = clamp(this.baseHealth + 6, 0, 100);
    events.push({ type: 'message', text: '空域暂时净空，补给完成。', tone: 'success' });
  }

  private applyWorldBounds(aircraft: AircraftState, dt: number, events: SimEvent[]): void {
    const distance = horizontalDistance(aircraft.position, this.basePosition);
    if (distance <= WORLD_RADIUS) return;

    const over = distance - WORLD_RADIUS;
    const toCenter = this.basePosition.clone().sub(aircraft.position);
    toCenter.y = 0;
    toCenter.normalize();
    aircraft.position.addScaledVector(toCenter, over * dt * 0.72);
    if (aircraft.id === PLAYER_ID) {
      aircraft.health = clamp(aircraft.health - over * dt * 0.002, 0, aircraft.maxHealth);
      if (this.boundaryWarnCooldown <= 0) {
        events.push({ type: 'message', text: '正在离开战区，自动引导返航。', tone: 'warn' });
        this.boundaryWarnCooldown = 3.5;
      }
    }
  }

  private checkGameOver(events: SimEvent[]): void {
    if (this.mode !== 'playing') return;
    const playerDown = this.player.health <= 0;
    const baseDown = this.baseHealth <= 0;
    if (!playerDown && !baseDown) return;

    this.mode = 'gameover';
    this.player.alive = false;
    const victory = false;
    this.statusText = playerDown ? '座机被击落' : '航母战斗群失守';
    events.push({ type: 'game-over', victory, score: this.score, wave: this.wave, reason: playerDown ? 'shotdown' : 'base-lost' });
  }

  private isPlayerOnCarrierDeck(): boolean {
    const rel = this.player.position.clone().sub(this.basePosition);
    const mainDeck =
      rel.x > -86 && rel.x < 82 &&
      rel.z > -228 && rel.z < 228 &&
      rel.y >= 10 && rel.y <= 42;
    const angledDeck =
      rel.x > -116 && rel.x < 28 &&
      rel.z > 10 && rel.z < 190 &&
      rel.y >= 10 && rel.y <= 42;
    return mainDeck || angledDeck;
  }

  private crashPlayer(reason: 'crash', events: SimEvent[], message: string): void {
    if (this.mode !== 'playing') return;
    this.mode = 'gameover';
    this.player.alive = false;
    this.player.health = 0;
    this.statusText = '座机坠毁';
    events.push({ type: 'explosion', position: this.player.position.clone(), radius: 96, tone: 'large' });
    events.push({ type: 'message', text: message, tone: 'danger' });
    events.push({ type: 'game-over', victory: false, score: this.score, wave: this.wave, reason });
  }
}

export { BASE_ID, PLAYER_ID, WORLD_RADIUS };
